import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

import type { UUID } from '@/types/Id'

export type OrgsOverviewSelectionMode = 'all' | 'custom'

interface OrgsOverviewSelectionState {
  selectionMode: OrgsOverviewSelectionMode
  selectedOrgIds: UUID[]
  scopeUserId: string | null
  setSelectedOrgIds: (ids: UUID[]) => void
  toggleOrgId: (id: UUID) => void
  resetToAll: () => void
  clearSelection: () => void
  // Reset the selection if the persisted scope belongs to a different user.
  syncScopeToUser: (userId: string) => void
}

const STORAGE_KEY = 'sweetspot-orgs-overview-selection'
const STORAGE_VERSION = 4

type PersistedSelection = Pick<
  OrgsOverviewSelectionState,
  'selectionMode' | 'selectedOrgIds' | 'scopeUserId'
>

const normalizePersistedSelection = ({
  selectionMode,
  selectedOrgIds,
  scopeUserId,
}: PersistedSelection): PersistedSelection => {
  if (selectionMode === 'custom' && selectedOrgIds.length > 0) {
    return { selectionMode: 'custom', selectedOrgIds, scopeUserId }
  }
  return { selectionMode: 'all', selectedOrgIds: [], scopeUserId }
}

const getMigratedState = (persisted: unknown): PersistedSelection => {
  if (!persisted || typeof persisted !== 'object') {
    return { selectionMode: 'all', selectedOrgIds: [], scopeUserId: null }
  }

  const state = persisted as Partial<OrgsOverviewSelectionState>
  const selectedOrgIds = Array.isArray(state.selectedOrgIds)
    ? state.selectedOrgIds
    : []

  const selectionMode =
    state.selectionMode === 'custom'
      ? 'custom'
      : selectedOrgIds.length > 0
        ? 'custom'
        : 'all'

  const scopeUserId =
    typeof state.scopeUserId === 'string' ? state.scopeUserId : null

  return normalizePersistedSelection({
    selectionMode,
    selectedOrgIds,
    scopeUserId,
  })
}

export const useOrgsOverviewSelectionStore =
  create<OrgsOverviewSelectionState>()(
    persist(
      (set) => ({
        selectionMode: 'all',
        selectedOrgIds: [],
        scopeUserId: null,
        setSelectedOrgIds: (ids) =>
          set((state) => ({
            selectionMode: 'custom',
            selectedOrgIds: ids,
            scopeUserId: state.scopeUserId,
          })),
        toggleOrgId: (id) =>
          set((state) => ({
            selectionMode: 'custom',
            selectedOrgIds: state.selectedOrgIds.includes(id)
              ? state.selectedOrgIds.filter((existing) => existing !== id)
              : [...state.selectedOrgIds, id],
          })),
        resetToAll: () =>
          set((state) => ({
            selectionMode: 'all',
            selectedOrgIds: [],
            scopeUserId: state.scopeUserId,
          })),
        clearSelection: () =>
          set((state) => ({
            selectionMode: 'custom',
            selectedOrgIds: [],
            scopeUserId: state.scopeUserId,
          })),
        syncScopeToUser: (userId) =>
          set((state) => {
            if (state.scopeUserId === userId) return state
            return {
              selectionMode: 'all',
              selectedOrgIds: [],
              scopeUserId: userId,
            }
          }),
      }),
      {
        name: STORAGE_KEY,
        version: STORAGE_VERSION,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => normalizePersistedSelection(state),
        migrate: (persisted) => getMigratedState(persisted),
      },
    ),
  )

export const useOrgsOverviewSelectionMode = () =>
  useOrgsOverviewSelectionStore((s) => s.selectionMode)

export const useSelectedOrgIds = () =>
  useOrgsOverviewSelectionStore(useShallow((s) => s.selectedOrgIds))

export const useOrgsOverviewSelection = () =>
  useOrgsOverviewSelectionStore(
    useShallow((s) => ({
      selectionMode: s.selectionMode,
      selectedOrgIds: s.selectedOrgIds,
    })),
  )

export const useSetSelectedOrgIds = () =>
  useOrgsOverviewSelectionStore((s) => s.setSelectedOrgIds)

export const useToggleSelectedOrgId = () =>
  useOrgsOverviewSelectionStore((s) => s.toggleOrgId)

export const useResetOrgSelectionToAll = () =>
  useOrgsOverviewSelectionStore((s) => s.resetToAll)

export const useClearOrgSelection = () =>
  useOrgsOverviewSelectionStore((s) => s.clearSelection)

export const useClearSelectedOrgIds = () =>
  useOrgsOverviewSelectionStore((s) => s.clearSelection)

export const useSyncOrgsOverviewScopeToUser = () =>
  useOrgsOverviewSelectionStore((s) => s.syncScopeToUser)
